import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import {
  Add,
  Check,
  Close,
  Delete,
  Edit,
  LocationOn,
  MyLocation,
  Star,
  StarBorder,
} from "@mui/icons-material"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ClientAddress,
  useCreateClientAddressMutation,
  useDeleteClientAddressMutation,
  useUpdateClientAddressMutation,
} from "../../apis/appApi"

type AddressSectionProps = {
  clientId: number
  addresses: ClientAddress[]
  onSuccess: (message: string) => void
}

type AddressFormState = {
  addressId: number | null // null = new
  streetNumber: string
  streetName: string
  appartNumber: string
  city: string
  state: string
  country: string
  postcode: string
  label: string
  notes: string
}

const emptyForm: AddressFormState = {
  addressId: null,
  streetNumber: "",
  streetName: "",
  appartNumber: "",
  city: "",
  state: "",
  country: "Canada",
  postcode: "",
  label: "",
  notes: "",
}

export const AddressSection: FC<AddressSectionProps> = ({
  clientId,
  addresses,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [createAddr, { isLoading: isCreating }] = useCreateClientAddressMutation()
  const [updateAddr, { isLoading: isUpdating }] = useUpdateClientAddressMutation()
  const [deleteAddr] = useDeleteClientAddressMutation()

  const [editForm, setEditForm] = useState<AddressFormState | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ClientAddress | null>(null)

  const isSaving = isCreating || isUpdating

  const handleStartAdd = useCallback(() => {
    setEditForm({ ...emptyForm })
  }, [])

  const handleStartEdit = useCallback((ca: ClientAddress) => {
    const a = ca.address
    if (!a) return
    setEditForm({
      addressId: a.id,
      streetNumber: a.streetNumber || "",
      streetName: a.streetName || "",
      appartNumber: a.appartNumber || "",
      city: a.city || "",
      state: a.state || "",
      country: a.country || "",
      postcode: a.postcode || "",
      label: a.label || "",
      notes: a.notes || "",
    })
  }, [])

  const handleCancel = useCallback(() => {
    setEditForm(null)
  }, [])

  const updateField = useCallback(
    (field: keyof AddressFormState, value: string) => {
      setEditForm((prev) => (prev ? { ...prev, [field]: value } : prev))
    },
    [],
  )

  const isFormValid =
    editForm !== null &&
    editForm.streetNumber.trim() !== "" &&
    editForm.streetName.trim() !== "" &&
    editForm.city.trim() !== "" &&
    editForm.state.trim() !== "" &&
    editForm.country.trim() !== "" &&
    editForm.postcode.trim() !== ""

  const handleSave = useCallback(async () => {
    if (!editForm || !isFormValid) return

    const body = {
      streetNumber: editForm.streetNumber.trim(),
      streetName: editForm.streetName.trim(),
      appartNumber: editForm.appartNumber.trim() || null,
      city: editForm.city.trim(),
      state: editForm.state.trim(),
      country: editForm.country.trim(),
      postcode: editForm.postcode.trim(),
      label: editForm.label.trim() || null,
      notes: editForm.notes.trim() || null,
    }

    try {
      if (editForm.addressId === null) {
        await createAddr({ clientId, ...body }).unwrap()
        onSuccess(t("clients.address.added"))
      } else {
        await updateAddr({ clientId, addressId: editForm.addressId, ...body }).unwrap()
        onSuccess(t("clients.address.updated"))
      }
      setEditForm(null)
    } catch {
      // Error handled by RTK Query
    }
  }, [editForm, isFormValid, clientId, createAddr, updateAddr, onSuccess, t])

  const handleDelete = useCallback(async () => {
    if (!deleteTarget?.address) return
    try {
      await deleteAddr({ clientId, addressId: deleteTarget.address.id }).unwrap()
      onSuccess(t("clients.address.deleted"))
      setDeleteTarget(null)
    } catch {
      // Error handled by RTK Query
    }
  }, [deleteTarget, clientId, deleteAddr, onSuccess, t])

  const renderForm = (isNew: boolean) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        bgcolor: isNew ? "background.paper" : undefined,
        borderColor: "primary.light",
        borderStyle: "dashed",
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label={t("clients.address.streetNumber")}
            value={editForm?.streetNumber || ""}
            onChange={(e) => updateField("streetNumber", e.target.value)}
            required
            sx={{ width: 100 }}
            autoFocus={isNew}
          />
          <TextField
            size="small"
            label={t("clients.address.streetName")}
            value={editForm?.streetName || ""}
            onChange={(e) => updateField("streetName", e.target.value)}
            required
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label={t("clients.address.appartNumber")}
            value={editForm?.appartNumber || ""}
            onChange={(e) => updateField("appartNumber", e.target.value)}
            sx={{ width: 90 }}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label={t("clients.address.city")}
            value={editForm?.city || ""}
            onChange={(e) => updateField("city", e.target.value)}
            required
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label={t("clients.address.state")}
            value={editForm?.state || ""}
            onChange={(e) => updateField("state", e.target.value)}
            required
            sx={{ width: 100 }}
          />
          <TextField
            size="small"
            label={t("clients.address.postcode")}
            value={editForm?.postcode || ""}
            onChange={(e) => updateField("postcode", e.target.value)}
            required
            sx={{ width: 110 }}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label={t("clients.address.country")}
            value={editForm?.country || ""}
            onChange={(e) => updateField("country", e.target.value)}
            required
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label={t("clients.address.label")}
            value={editForm?.label || ""}
            onChange={(e) => updateField("label", e.target.value)}
            sx={{ flex: 1 }}
          />
        </Stack>
        <TextField
          size="small"
          label={t("clients.address.notes")}
          value={editForm?.notes || ""}
          onChange={(e) => updateField("notes", e.target.value)}
          multiline
          rows={2}
          fullWidth
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={handleCancel} disabled={isSaving}>
            {t("cancel")}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <Check />}
            onClick={handleSave}
            disabled={!isFormValid || isSaving}
          >
            {t("save")}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )

  return (
    <Paper {...uiSurface("UI-DLV2-088")} variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ color: "text.secondary", display: "flex" }}>
          <LocationOn fontSize="small" />
        </Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {t("clients.detail.addresses")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({addresses.length})
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        {addresses.map((ca, index) => {
          const a = ca.address
          if (!a) return null
          const isEditing = editForm?.addressId === a.id
          const isPrimary = index === 0

          if (isEditing) {
            return <Box key={ca.id}>{renderForm(false)}</Box>
          }

          const hasGeocode = a.position?.latitude != null && a.position?.longitude != null

          return (
            <Paper
              key={ca.id}
              variant="outlined"
              sx={{
                p: 1.5,
                "&:hover .addr-actions": { opacity: 1 },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                  {/* F089: Primary address star indicator */}
                  <Tooltip
                    title={isPrimary ? t("clients.address.primary") : t("clients.address.primaryHint")}
                  >
                    <Box sx={{ display: "flex", color: isPrimary ? "warning.main" : "action.disabled", mt: 0.25 }}>
                      {isPrimary ? (
                        <Star sx={{ fontSize: 18 }} />
                      ) : (
                        <StarBorder sx={{ fontSize: 18 }} />
                      )}
                    </Box>
                  </Tooltip>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2">
                      {[a.streetNumber, a.streetName].filter(Boolean).join(" ")}
                      {a.appartNumber ? `, ${t("clients.address.appartNumber")} ${a.appartNumber}` : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[a.city, a.state, a.postcode].filter(Boolean).join(", ")}
                      {a.country ? `, ${a.country}` : ""}
                    </Typography>
                    {a.label && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: "block" }}>
                        {a.label}
                      </Typography>
                    )}
                    {a.notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.25, display: "block", fontStyle: "italic" }}
                      >
                        {a.notes}
                      </Typography>
                    )}

                    {/* F091: Address geocoding display */}
                    {hasGeocode && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <MyLocation sx={{ fontSize: 14, color: "success.main" }} />
                        <Chip
                          size="small"
                          variant="outlined"
                          color="success"
                          label={`${a.position!.latitude.toFixed(5)}, ${a.position!.longitude.toFixed(5)}`}
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      </Stack>
                    )}
                    {!hasGeocode && a.positionId == null && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <MyLocation sx={{ fontSize: 14, color: "action.disabled" }} />
                        <Typography variant="caption" color="text.disabled">
                          {t("clients.address.notGeocoded")}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>

                <Stack
                  className="addr-actions"
                  direction="row"
                  sx={{ opacity: 0, transition: "opacity 0.15s", flexShrink: 0 }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleStartEdit(ca)}
                    disabled={editForm !== null}
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteTarget(ca)}
                    disabled={editForm !== null}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          )
        })}

        {/* Add new address form */}
        <Collapse in={editForm?.addressId === null}>
          {editForm?.addressId === null && renderForm(true)}
        </Collapse>

        {addresses.length === 0 && editForm === null && (
          <Typography variant="body2" color="text.disabled">
            {t("clients.address.noAddresses")}
          </Typography>
        )}
      </Stack>

      {editForm === null && (
        <Button
          size="small"
          startIcon={<Add />}
          onClick={handleStartAdd}
          sx={{ mt: 1, textTransform: "none" }}
        >
          {t("clients.address.add")}
        </Button>
      )}

      {/* Delete confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-089") }}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
      >
        <DialogTitle>{t("clients.address.deleteConfirm")}</DialogTitle>
        <DialogContent>
          {deleteTarget?.address && (
            <Typography variant="body2">
              {[
                deleteTarget.address.streetNumber,
                deleteTarget.address.streetName,
                deleteTarget.address.city,
              ]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t("cancel")}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
